import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Share2, Send, Flag, Users } from "lucide-react";
import api, { API, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { backendUrl } from "@/components/SongCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function PerformanceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [rec, setRec] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);

  useEffect(() => {
    api.get(`/recordings/${id}`).then(({ data }) => { setRec(data); setLikes(data.likes || 0); }).catch(() => setRec(false));
    api.get(`/recordings/${id}/comments`).then(({ data }) => setComments(data.items));
  }, [id]);

  const like = async () => {
    if (!user) { toast.error("Log in to like."); return; }
    const { data } = await api.post(`/recordings/${id}/like`); setLiked(data.liked); setLikes(data.likes);
  };
  const comment = async (e) => {
    e.preventDefault(); if (!user) { toast.error("Log in to comment."); return; }
    if (!text.trim()) return;
    try { const { data } = await api.post(`/recordings/${id}/comments`, { text }); setComments([data, ...comments]); setText(""); } catch (er) { toast.error(apiErr(er)); }
  };
  const report = async () => {
    try { await api.post("/reports", { target_type: "recording", target_id: id, reason: "inappropriate" }); toast.success("Report submitted."); } catch (e) { toast.error(apiErr(e)); }
  };
  const share = () => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied!"); };

  if (rec === false) return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-muted-foreground">This performance is unavailable or private.</div>;
  if (!rec) return <div className="min-h-[50vh]" />;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <div className="glass rounded-3xl overflow-hidden mb-6">
        <div className="relative aspect-video"><img src={rec.cover_url} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" /></div>
        <div className="p-6">
          <h1 className="font-head text-2xl font-black">{rec.song_title}</h1>
          <p className="text-muted-foreground">{rec.song_artist}</p>
          {rec.owner && <Link to={`/@${rec.owner.username}`} className="inline-flex items-center gap-2 mt-3 text-sm">
            <span className="w-7 h-7 rounded-full bg-neon-purple/30 flex items-center justify-center text-xs overflow-hidden">{rec.owner.avatar_url ? <img src={backendUrl(rec.owner.avatar_url)} className="w-full h-full object-cover" alt="" /> : rec.owner.username[0].toUpperCase()}</span>
            @{rec.owner.username}
          </Link>}
          <audio controls src={`${API}/recordings/${id}/audio`} className="w-full mt-5" data-testid="perf-audio" />
          {rec.duet_open && (
            <Button onClick={() => user ? nav(`/duet/${id}`) : nav("/login", { state: { from: `/duet/${id}` } })} data-testid="sing-duet-btn"
              className="w-full rounded-full bg-neon-cyan text-black hover:bg-neon-cyan/90 font-bold py-6 mt-4"><Users className="w-5 h-5 mr-2" /> Sing this Duet</Button>
          )}
          <div className="flex items-center gap-2 mt-4">
            <Button onClick={like} variant="outline" className="rounded-full glass border-white/20" data-testid="perf-like"><Heart className={`w-4 h-4 mr-2 ${liked ? "fill-neon-pink text-neon-pink" : ""}`} /> {likes}</Button>
            <Button onClick={share} variant="outline" className="rounded-full glass border-white/20"><Share2 className="w-4 h-4 mr-2" /> Share</Button>
            <Button onClick={report} variant="ghost" className="rounded-full ml-auto text-muted-foreground" data-testid="perf-report"><Flag className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>

      <div className="card-surface rounded-2xl p-6">
        <h3 className="font-head font-bold mb-4">Comments</h3>
        <form onSubmit={comment} className="flex gap-2 mb-5">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment..." className="bg-input border-white/10 rounded-full" data-testid="comment-input" />
          <Button type="submit" size="icon" className="rounded-full bg-neon-pink flex-shrink-0" data-testid="comment-submit"><Send className="w-4 h-4" /></Button>
        </form>
        {comments.length === 0 ? <p className="text-sm text-muted-foreground">No comments yet.</p> : (
          <div className="space-y-3">{comments.map((c) => (
            <div key={c.comment_id} className="flex gap-3"><div className="w-8 h-8 rounded-full bg-neon-purple/30 flex items-center justify-center text-xs flex-shrink-0">{c.username[0].toUpperCase()}</div>
              <div><p className="text-sm"><Link to={`/@${c.username}`} className="font-semibold">@{c.username}</Link> <span className="text-muted-foreground">{c.text}</span></p></div></div>
          ))}</div>
        )}
      </div>
    </div>
  );
}
